import { createGenerateClassName, StylesProvider } from '@mui/styles';
import Loader from 'apollo-react/components/Loader';
import ApolloThemeProvider from 'apollo-react/utils/ApolloThemeProvider';
import isString from 'lodash/isString';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import { failSSOLogin, finishSignOff, syncTokenStatus, verifyAccess } from './actions/AuthActions';
import { handleErrors } from './actions/ErrorActions';
import { getUICacheObjects } from './api/ui-cache-objects';
import App from './App';
import * as serviceWorkerRegistration from './registerServiceWorker';
import { persistor, store } from './store';
import { setUserCacheKey } from './utils/cacheUtils';
import { initChildrenWindows } from './utils/childrenWindowsUtils';
import { isTrue } from './utils/commonUtils';
import { getPrefixedKey } from './utils/envLocalStorage';
import { isSet, parseNilOrValue } from './utils/fns';
import { getQsParam } from './utils/qs';
import './apolloStyles.css';
import { isSSOPopupWindow } from './utils/ssoPopupUtil';
import customTheme from './utils/themeUtils';
import { handleGetUICacheObjectsResponse } from './utils/uiCacheObjects.utils';

const generateClassName = createGenerateClassName({
  productionPrefix: 'ss',
});

const lowerCaseUrl = window.location.href.toLowerCase();
const searchUrl = new URL(lowerCaseUrl);
localStorage.setItem('tenant', searchUrl.searchParams.get('domain'));
localStorage.setItem('autoAuthentication', true);

const token = getQsParam('Token');
const isPopupWindow = isSSOPopupWindow();
const appMain = isPopupWindow ? (
  <Loader />
) : (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <ApolloThemeProvider theme={customTheme}>
        <StylesProvider generateClassName={generateClassName}>
          <App />
        </StylesProvider>
      </ApolloThemeProvider>
    </PersistGate>
  </Provider>
);

initChildrenWindows();

if (isPopupWindow && token) {
  const errorCode = getQsParam('errorCode', '');
  const errorMessage = getQsParam('errorMessage', '');
  const hasErrorRaw = getQsParam('hasError', false);
  const hasError = isString(hasErrorRaw) ? /true/i.test(hasErrorRaw) : hasErrorRaw;
  const storedToken = parseNilOrValue(localStorage.getItem('token'));
  const errorResponse = JSON.stringify({
    errorCode,
    errorMessage,
    hasError,
  });

  if (isSet(storedToken)) {
    localStorage.setItem('signOffResponse', errorResponse);
  } else if (hasError) {
    localStorage.setItem('loginFailureResponse', errorResponse);
  } else {
    const actorCode = getQsParam('ActorCode');
    const actorCodeWithDomain = getQsParam('ActorCodeWithDomain', '');
    const licId = getQsParam('licId');
    const sessionExpirationErrorCode = getQsParam('sessionExpirationErrorCode');
    const sessionExpirationErrorMessage = getQsParam('sessionExpirationErrorMessage');
    const username = getQsParam('UserFullName');
    const actorFirstName = getQsParam('ActorFirstName');
    const actorLastName = getQsParam('ActorLastName');
    const culture = getQsParam('culture');
    const loggedOnRoleCode = getQsParam('LoggedOnRoleCode');
    const requireRegistration = getQsParam('ExternalUserNeedsRegistration');
    const isExternalUser = getQsParam('IsExternalUser');
    const isArchiveUser = getQsParam('IsArchiveUser');
    const tokenLifeSpan = getQsParam('TokenLifeSpan');
    const smartInsightApplicationURL = getQsParam('SmartInsightApplicationURL');
    const maxAllowedContentLength = getQsParam('MaxAllowedContentLength');

    localStorage.setItem('actorCode', actorCode);
    localStorage.setItem('actorCodeWithDomain', actorCodeWithDomain);
    localStorage.setItem('licId', licId);
    localStorage.setItem('sessionExpirationErrorCode', sessionExpirationErrorCode);
    localStorage.setItem('sessionExpirationErrorMessage', sessionExpirationErrorMessage);
    localStorage.setItem('token', token);
    localStorage.setItem('userFullName', username);
    localStorage.setItem('actorFirstName', actorFirstName);
    localStorage.setItem('actorLastName', actorLastName);
    culture && localStorage.setItem('culture', culture);
    localStorage.setItem('loggedOnRoleCode', loggedOnRoleCode);
    localStorage.setItem('requireRegistration', isTrue(requireRegistration));
    localStorage.setItem('isExternalUser', isTrue(isExternalUser));
    localStorage.setItem('isArchiveUser', isTrue(isArchiveUser));
    localStorage.setItem('tokenLifeSpan', tokenLifeSpan);
    localStorage.setItem('isUserLoggedTemp', 'true');
    localStorage.setItem('smartInsightApplicationURL', smartInsightApplicationURL);
    localStorage.setItem('maxAllowedContentLength', maxAllowedContentLength);
    setUserCacheKey(actorCodeWithDomain);
  }

  window.close();
} else {
  window.addEventListener('storage', ({ key, value, newValue }) => {
    if (key === getPrefixedKey('signOffResponse')) {
      store.dispatch(finishSignOff());
    }

    if (key === getPrefixedKey('token')) {
      store.dispatch(verifyAccess(value)); // TODO: 'value' is undefined, use 'newValue' instead
    }

    if (key === getPrefixedKey('loginFailureResponse')) {
      store.dispatch(failSSOLogin());
    }

    if (key == getPrefixedKey('isTokenUpdated')) {
      store.dispatch(syncTokenStatus());
    }

    if (key === getPrefixedKey('isUserLoggedTemp') && newValue === 'true') {
      localStorage.removeItem('isUserLoggedTemp');

      getUICacheObjects().then((response) => {
        if (response.hasError) {
          store.dispatch(handleErrors(response.errors));
          return;
        }

        handleGetUICacheObjectsResponse(response);
      });
    }
  });

  store.dispatch(verifyAccess(token));
}

ReactDOM.render(appMain, document.getElementById('root'));

serviceWorkerRegistration.register();
